<template>

    <section :id="component.identifier ? component.identifier : ''" class="mb-64" v-if="show" ref="container">

        <component-header :title="title" :description="description"></component-header>

        <div class="site__container">
            <div :class="[ containerNegativeMargin ]">

                <ul v-if="style === 'pyramid'" class=" mx-auto flex flex-wrap -mt-16 pb-48" :class="[ containerWidth ]">
                    <li
                        v-for="(member, index) in component.members"
                        :key="member.id"
                        class="px-8 flex-none pt-16"
                        :class="[ pyramidItemWidth, getRelativePositioningClass(index) ]"
                        style="top: 48px;"
                    >
                        <div class="w-full relative overflow-hidden site__rounded--medium site__bg--brand" style="padding-top: 200%;">

                            <div class="absolute inset-0">

                                <div
                                    class="w-full h-1/2 bg-primary bg-center bg-cover bg-no-repeat"
                                    :style="{ 'background-image': 'url(' + member.image_url + '&w=425&fit=max)' }"
                                ></div>

                                <div class="w-full h-1/2 px-48 py-32 flex flex-col justify-center site__color--brand-contrast">
                                    <team-socials :member="member"></team-socials>
                                    <h2 class="text-heading-2 font-semibold mb-4">{{ member.first_name + ' ' + member.last_name }}</h2>
                                    <p class="text-body italic mb-16">{{ translated(member.title) }}</p>
                                    <p class="text-body-sm">{{ translated(member.description) }}</p>
                                </div>

                            </div>
                        </div>
                    </li>
                </ul>

                <div v-else-if="style === 'grid'">

                    <component :is="'style'" v-html="customStyles"></component>

                    <ul class=" w-full flex flex-wrap site__rounded--medium overflow-hidden">
                        <li
                            v-for="(member, index) in component.members"
                            :key="member.id"
                            class="flex items-stretch group"
                            :class="[ gridItemWidth, getRowReverseClass(index) ]"
                        >
                            <div class="w-1/2 relative" style="padding-top: 50%">
                                <div
                                    class="absolute border-solid z-20 h-0 w-0 top-0"
                                    :class="[ gridTriangleVisibility, [3,0].includes((index+1) % 4) ? 'left-0 triangle-right' : 'right-0 triangle-left' ]"
                                    style="margin-top: calc(50% - 0.8rem)"
                                ></div>
                                <div class="trans-ease opacity-0 group-hover:opacity-100 absolute inset-0 w-full h-full site__bg--text-80 site__color--brand-contrast text-body-sm p-32 flex items-center justify-center">
                                    <p>{{ translated(member.description) }}</p>
                                </div>
                                <div
                                    class="absolute inset-0 w-full h-full bg-center bg-cover bg-no-repeat"
                                    :style="{ 'background-image': 'url(' + member.image_url + '&w=490&fit=max)' }"
                                ></div>
                            </div>

                            <div class="w-1/2 site__bg--brand flex flex-col items-center justify-center p-32 site__color--brand-contrast text-center">
                                <team-socials :member="member"></team-socials>
                                <h2 class="text-heading-1 font-semibold mb-8">{{ member.first_name + ' ' + member.last_name }}</h2>
                                <p class="text-body mb-16">{{ translated(member.title) }}</p>
                            </div>
                        </li>
                    </ul>
                </div>

                <ul v-else class=" mx-auto flex flex-wrap justify-center -mt-48" :class="[ containerWidth ]">
                    <li
                        v-for="(member, index) in component.members"
                        :key="member.id"
                        class="px-8 flex-none pt-48"
                        :class="[ classicItemWidth ]"
                    >
                        <div class="w-full px-32">

                            <div
                                class="w-full mb-24 site__rounded--large relative bg-center bg-cover bg-no-repeat"
                                style="padding-top: 100%;"
                                :style="{ 'background-image': 'url(' + member.image_url + '&w=430&fit=max)' }"
                            ></div>

                            <div class="flex flex-col items-center text-center">
                                <team-socials :member="member"></team-socials>
                                <h2 class="text-heading-1 font-semibold mb-4">{{ member.first_name + ' ' + member.last_name }}</h2>
                                <p class="text-body italic mb-16">{{ translated(member.title) }}</p>
                                <p class="text-body-sm">{{ translated(member.description) }}</p>
                            </div>
                        </div>
                    </li>
                </ul>

            </div>
        </div>

    </section>

</template>

<script>
import ComponentHeader from './ComponentHeader.vue';
import TeamSocials from './TeamSocials.vue';
import { mapState } from 'pinia';
import { useCommonStore } from '../../state/common';
import { useBrandingStore } from '../../state/branding';
import { responsive, runningInBrowser } from '../../helpers';

export default {
    components: {
        'component-header': ComponentHeader,
        'team-socials': TeamSocials,
    },
    props: {
        component: {
            required: true,
        }
    },
    data: function () {
        return {
            style: 'classic',
        }
    },
    computed: {
        ...mapState(useCommonStore, ['locale']),
        ...mapState(useBrandingStore, ['preview', 'brandHSLA']),
        show () {
            return runningInBrowser() || this.hasContent;
        },
        hasContent () {
            return this.component && this.component.members && !!this.component.members.length;
        },
        containerNegativeMargin () {
            return responsive(this.preview.type, 'lg:-mx-8');
        },
        gridItemWidth () {
            return responsive(this.preview.type, 'w-full lg:w-1/2');
        },
        gridTriangleVisibility () {
            return responsive(this.preview.type, 'hidden lg:block');
        },
        pyramidItemWidth () {
            return responsive(this.preview.type, 'w-full sm:w-1/2 lg:w-1/3');
        },
        classicItemWidth () {
            return responsive(this.preview.type, 'w-full sm:w-1/2 lg:w-1/3');
        },
        containerWidth () {
            return responsive(this.preview.type, 'w-full xs:w-3/4 sm:w-full md:w-5/6 mdplus:w-3/4 lg:w-full');
        },
        customStyles () {
            return `.triangle-left {
                border-width: 8px 8px 8px 0;
                border-color: transparent ${ this.brandHSLA } transparent transparent;
            }
            .triangle-right {
                border-width: 8px 0 8px 8px;
                border-color: transparent transparent transparent ${ this.brandHSLA };
            }`
        },
        // style() {
        //     return this.component.style;
        // },
        title: {
            get () {
                return this.translated(this.component.title);
            }
        },
        description: {
            get () {
                return this.translated(this.component.description);
            }
        }
    },
    methods: {
        getRowReverseClass (index) {

            let c = ! ((index + 1) % 2) ? 'flex-row-reverse ' : 'flex-row ';

            c += [3,0].includes((index + 1) % 4) ? 'lg:flex-row-reverse' : 'lg:flex-row';

            return responsive(this.preview.type, c);
        },
        getRelativePositioningClass (index) {

            let c = index % 2 ? 'sm:relative ' : '';

            if (!index || !((index-2) % 3) || !((index-3) % 3)) {
                c+= 'lg:relative';
            } else {
                c+= 'lg:static';
            }

            return responsive(this.preview.type, c);
        }
    },
    mounted () {
        this.$emit('loaded');
    }
}
</script>

<style scoped>
</style>
